import React, { useState, useEffect } from 'react';
import { ChartBarIcon as BarChart3, ClockIcon as Clock, CalendarIcon as Calendar, ArrowTrendingUpIcon as TrendingUp, ComputerDesktopIcon as Seat, ClockIcon as Timer } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

interface DailyStat {
  date: string;
  minutes: number;
  sessions: number;
  seats_used: number;
}

interface Statistics {
  period_days: number;
  total_minutes: number;
  total_hours: number;
  total_sessions: number;
  average_session_length: number;
  daily_stats: DailyStat[];
}

const TimeStatistics: React.FC = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState(7);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/v1/time-tracking/statistics?days=${selectedPeriod}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('통계를 가져올 수 없습니다');
        }

        const data = await response.json();
        if (data.success) {
          setStatistics(data.statistics);
        }
      } catch (err) {
        console.error('Statistics fetch error:', err);
        setError(err instanceof Error ? err.message : '알 수 없는 오류');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatistics();
  }, [selectedPeriod]);

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}분`;
    if (mins === 0) return `${hours}시간`;
    return `${hours}시간 ${mins}분`;
  };

  const getMaxDailyMinutes = () => {
    if (!statistics?.daily_stats.length) return 0;
    return Math.max(...statistics.daily_stats.map(stat => stat.minutes));
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">통계 로딩 중...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-red-600">
          <p>통계를 불러오는데 실패했습니다: {error}</p>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">
          <p>통계 데이터가 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <BarChart3 className="h-6 w-6 text-blue-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">사용 통계</h2>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(Number(e.target.value))}
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value={7}>7일간</option>
          <option value={14}>14일간</option>
          <option value={30}>30일간</option>
        </select>
      </div>

      {/* 요약 통계 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <Clock className="h-8 w-8 text-blue-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-blue-900">{statistics.total_hours.toFixed(1)}</div>
          <div className="text-sm text-blue-700">총 시간</div>
        </div>

        <div className="bg-green-50 rounded-lg p-4 text-center">
          <Calendar className="h-8 w-8 text-green-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-green-900">{statistics.total_sessions}</div>
          <div className="text-sm text-green-700">총 세션</div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <TrendingUp className="h-8 w-8 text-purple-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-purple-900">{statistics.average_session_length.toFixed(1)}</div>
          <div className="text-sm text-purple-700">평균 시간 (분)</div>
        </div>

        <div className="bg-orange-50 rounded-lg p-4 text-center">
          <Timer className="h-8 w-8 text-orange-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-orange-900">
            {(statistics.total_minutes / statistics.period_days).toFixed(0)}
          </div>
          <div className="text-sm text-orange-700">일평균 (분)</div>
        </div>
      </div>

      {/* 일별 사용량 차트 */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">일별 사용량</h3>
        <div className="space-y-3">
          {statistics.daily_stats.map((stat, index) => {
            const maxMinutes = getMaxDailyMinutes();
            const percentage = maxMinutes > 0 ? (stat.minutes / maxMinutes) * 100 : 0;
            const date = new Date(stat.date);
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <div key={stat.date} className="flex items-center">
                <div className="w-16 text-sm text-gray-600">
                  {date.toLocaleDateString('ko-KR', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                  {isToday && <span className="block text-xs text-blue-600">오늘</span>}
                </div>
                
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 rounded-full h-6 relative">
                    <div
                      className={`h-6 rounded-full transition-all duration-300 ${
                        isToday ? 'bg-blue-500' : 'bg-gray-400'
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                    <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                      {stat.minutes > 0 && formatTime(stat.minutes)}
                    </div>
                  </div>
                </div>
                
                <div className="w-20 text-right text-sm text-gray-600">
                  {stat.sessions > 0 && (
                    <span>{stat.sessions}회</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 사용 패턴 분석 */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-3">사용 패턴 분석</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">가장 많이 사용한 날:</span>
            <span className="ml-2 text-gray-900">
              {statistics.daily_stats.reduce((max, stat) => 
                stat.minutes > max.minutes ? stat : max
              ).date && new Date(
                statistics.daily_stats.reduce((max, stat) => 
                  stat.minutes > max.minutes ? stat : max
                ).date
              ).toLocaleDateString('ko-KR')}
            </span>
          </div>
          
          <div>
            <span className="font-medium text-gray-700">최장 사용 시간:</span>
            <span className="ml-2 text-gray-900">
              {formatTime(Math.max(...statistics.daily_stats.map(stat => stat.minutes)))}
            </span>
          </div>
          
          <div>
            <span className="font-medium text-gray-700">사용일 수:</span>
            <span className="ml-2 text-gray-900">
              {statistics.daily_stats.filter(stat => stat.minutes > 0).length}일
            </span>
          </div>
          
          <div>
            <span className="font-medium text-gray-700">총 세션 수:</span>
            <span className="ml-2 text-gray-900">
              {statistics.total_sessions}회
            </span>
          </div>
        </div>
      </div>

      {/* 사용률 요약 */}
      {statistics.total_sessions > 0 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 text-blue-600 mr-2" />
              <span className="font-medium text-blue-900">효율성 점수</span>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-blue-900">
                {Math.min(100, Math.round((statistics.average_session_length / 180) * 100))}점
              </div>
              <div className="text-xs text-blue-700">
                (3시간 기준)
              </div>
            </div>
          </div>
          <div className="mt-2 text-sm text-blue-800">
            {statistics.average_session_length >= 180 
              ? '훌륭한 집중력을 보여주고 있습니다!' 
              : statistics.average_session_length >= 120
              ? '좋은 학습 패턴을 유지하고 있습니다.'
              : '더 긴 시간 집중해보세요!'}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeStatistics;